import React, { useEffect, useState, useMemo } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { CreditCard, GraduationCap, Mail, Phone, MapPin, BookOpen, Users, Trash2, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { gradeLevel, sortGradeKeys, LEVEL_LABELS, LEVEL_ORDER } from "../../lib/grades";

export default function AdminEnrollments() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [openProfile, setOpenProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/enrollments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openStudent = async (enrollment) => {
    setOpenProfile(enrollment);
    setProfileData(null);
    try {
      const [appRes, payRes] = await Promise.all([
        api.get(`/applications/${enrollment.application_id}`),
        api.get(`/admin/student-payments/${enrollment.id}`),
      ]);
      setProfileData({ app: appRes.data, payments: payRes.data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteApplication = async () => {
    if (!openProfile?.application_id) return;
    const name = `${openProfile.student?.first_name || ""} ${openProfile.student?.last_name || ""}`.trim();
    const confirmMsg = lang === "fr"
      ? `Supprimer définitivement la candidature de "${name}" ?\n\nCela supprimera l'élève, l'inscription et tout l'historique de paiements. Cette action est irréversible.`
      : `Permanently delete "${name}"'s application?\n\nThis removes the student, enrollment and full payment history. This action is irreversible.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/applications/${openProfile.application_id}`);
      toast.success(lang === "fr" ? `"${name}" supprimé` : `"${name}" deleted`);
      setOpenProfile(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US") : "—";

  // Group enrollments by level → grade
  const grouped = useMemo(() => {
    const byLevel = { primaire: {}, college: {}, lycee: {}, unknown: {} };
    for (const e of items) {
      const grade = (e.student?.grade || "").trim() || "—";
      const lvl = gradeLevel(grade);
      if (!byLevel[lvl][grade]) byLevel[lvl][grade] = [];
      byLevel[lvl][grade].push(e);
    }
    return byLevel;
  }, [items]);

  const [expanded, setExpanded] = useState({ primaire: true, college: true, lycee: true, unknown: false });

  return (
    <div data-testid="admin-enrollments">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-serif text-3xl tracking-tight mb-2">{t("admin.enrollments")}</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {lang === "fr"
          ? "Inscriptions regroupées par niveau (Primaire · Collège · Lycée) puis par classe. Cliquez sur une ligne pour voir le profil complet de l'élève."
          : "Enrollments grouped by level (Primary · Middle · High school) then by grade. Click a row to see the full student profile."}
      </p>

      {LEVEL_ORDER.map(level => {
        const grades = grouped[level] || {};
        const allEnrolls = Object.values(grades).flat();
        if (allEnrolls.length === 0 && level !== "unknown") return null;
        const labels = LEVEL_LABELS[level][lang];
        const isOpen = expanded[level];
        const sortedKeys = sortGradeKeys(Object.keys(grades));

        return (
          <Card className="mb-5" key={level} data-testid={`level-${level}`}>
            <CardContent className="p-0">
              <button
                onClick={() => setExpanded(s => ({ ...s, [level]: !isOpen }))}
                className="w-full p-5 flex items-center gap-4 hover:bg-secondary/30 transition-colors text-left"
              >
                <div className="flex-1">
                  <div className="font-display font-bold text-xl tracking-tight">{labels.title}</div>
                  <div className="text-xs text-muted-foreground">{labels.range} · {allEnrolls.length} {lang === "fr" ? (allEnrolls.length > 1 ? "élèves" : "élève") : (allEnrolls.length > 1 ? "students" : "student")}</div>
                </div>
                <ChevronRight className={`w-5 h-5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>

              {isOpen && (sortedKeys.length === 0 ? (
                <div className="border-t border-border p-6 text-center text-muted-foreground text-sm">
                  {lang === "fr" ? "Aucune inscription à ce niveau." : "No enrollments at this level."}
                </div>
              ) : sortedKeys.map(grade => {
                const enrolls = grades[grade];
                return (
                  <div key={grade} className="border-t border-border" data-testid={`grade-${grade}`}>
                    <div className="px-5 py-2 bg-secondary/30 flex items-center justify-between">
                      <span className="font-display font-semibold text-sm px-2.5 py-0.5 rounded-full bg-primary/10 text-primary">{grade}</span>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                        {enrolls.length} {lang === "fr" ? (enrolls.length > 1 ? "élèves" : "élève") : (enrolls.length > 1 ? "students" : "student")}
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {enrolls.map(e => (
                          <tr
                            key={e.id}
                            data-testid={`enrollment-row-${e.id}`}
                            onClick={() => openStudent(e)}
                            className="border-t border-border/40 hover:bg-secondary/20 cursor-pointer"
                          >
                            <td className="px-5 py-3 font-medium">{e.student?.first_name} {e.student?.last_name}</td>
                            <td className="px-3 py-3 text-xs">{e.family?.email}</td>
                            <td className="px-3 py-3 text-xs">{t(`payments.${e.payment_mode}`)}</td>
                            <td className="px-3 py-3">
                              <Badge variant={e.deposit_paid ? "default" : "outline"}>{e.deposit_paid ? "Acompte ✓" : "—"}</Badge>
                            </td>
                            <td className="px-3 py-3">
                              <Badge variant={e.deposit_paid ? "default" : "secondary"}>
                                {e.deposit_paid ? (lang === "fr" ? "Actif" : "Active") : (lang === "fr" ? "Inscrit" : "Enrolled")}
                              </Badge>
                            </td>
                            <td className="px-5 py-3 text-xs text-muted-foreground">{fmtDate(e.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }))}
            </CardContent>
          </Card>
        );
      })}

      {items.length === 0 && (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          {lang === "fr" ? "Aucune inscription pour le moment." : "No enrollments yet."}
        </CardContent></Card>
      )}

      <Dialog open={!!openProfile} onOpenChange={(v) => !v && setOpenProfile(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="student-profile-dialog">
          {openProfile && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight">
                  {openProfile.student?.first_name} {openProfile.student?.last_name}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {openProfile.student?.grade || "—"} · {t(`payments.${openProfile.payment_mode}`)}
                </p>
              </DialogHeader>

              {!profileData && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {lang === "fr" ? "Chargement…" : "Loading…"}
                </div>
              )}

              {profileData && (
                <div className="space-y-5">
                  {/* Super-admin delete action */}
                  {isSuper && (
                    <div className="flex justify-end -mb-2">
                      <Button
                        data-testid="delete-application-btn"
                        onClick={deleteApplication}
                        disabled={deleting}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        {deleting ? (lang === "fr" ? "Suppression…" : "Deleting…") : (lang === "fr" ? "Supprimer l'élève" : "Delete student")}
                      </Button>
                    </div>
                  )}
                  {/* Family */}
                  <Section icon={Users} title={lang === "fr" ? "Famille" : "Family"}>
                    <KV k={lang === "fr" ? "Parent" : "Parent"} v={`${profileData.app.family?.parent_first_name || ""} ${profileData.app.family?.parent_last_name || ""}`} />
                    <KV k={<><Mail className="w-3 h-3 inline mr-1" /> Email</>} v={profileData.app.family?.email} />
                    <KV k={<><Phone className="w-3 h-3 inline mr-1" /> {lang === "fr" ? "Téléphone" : "Phone"}</>} v={profileData.app.family?.phone} />
                    <KV k={<><MapPin className="w-3 h-3 inline mr-1" /> {lang === "fr" ? "Pays" : "Country"}</>} v={profileData.app.family?.country} />
                  </Section>

                  {/* Academic */}
                  <Section icon={GraduationCap} title={lang === "fr" ? "Profil scolaire" : "Academic profile"}>
                    <KV k={lang === "fr" ? "Niveau" : "Grade"} v={profileData.app.student?.grade} />
                    <KV k={lang === "fr" ? "Date de naissance" : "Date of birth"} v={fmtDate(profileData.app.student?.date_of_birth)} />
                    <KV k={lang === "fr" ? "École précédente" : "Previous school"} v={profileData.app.academic_background?.previous_school} />
                    <KV k={lang === "fr" ? "Moyenne" : "Average"} v={profileData.app.academic_background?.average_grade} />
                    <KV k={lang === "fr" ? "Français" : "French"} v={profileData.app.language_profile?.french_level} />
                    <KV k={lang === "fr" ? "Anglais" : "English"} v={profileData.app.language_profile?.english_level} />
                    <KV k="Track" v={profileData.app.program_choice ? t(`programs.${profileData.app.program_choice}`) : "—"} />
                  </Section>

                  {/* Courses */}
                  {profileData.payments?.courses?.length > 0 && (
                    <Section icon={BookOpen} title={lang === "fr" ? "Cours inscrits" : "Enrolled courses"}>
                      <ul className="space-y-1 text-sm">
                        {profileData.payments.courses.map(c => (
                          <li key={c.id} className="flex justify-between border-b border-border/40 pb-1">
                            <span>{c.name_fr}</span>
                            <span className="text-xs text-muted-foreground">{c.day} · {c.time}</span>
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}

                  {/* Payments */}
                  <Section icon={CreditCard} title={lang === "fr" ? "Paiements" : "Payments"}>
                    <KV k={lang === "fr" ? "Total annuel" : "Annual total"} v={`${fmt(profileData.payments?.annual_total)} €`} />
                    <KV k={lang === "fr" ? "Acompte versé" : "Deposit paid"} v={`${fmt(profileData.payments?.deposit_paid_amount)} €`} />
                    <KV k={lang === "fr" ? "Mensualités réglées" : "Installments paid"} v={`${profileData.payments?.installments_paid || 0} / ${profileData.payments?.installments_count || 0}`} />
                    <KV k={lang === "fr" ? "Solde restant" : "Remaining"} v={`${fmt(profileData.payments?.balance_remaining)} €`} />
                    {profileData.payments?.transactions?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                          {lang === "fr" ? "Historique" : "History"}
                        </div>
                        <ul className="space-y-1 text-xs">
                          {profileData.payments.transactions.map(tx => (
                            <li key={tx.id} className="flex justify-between border-b border-border/30 pb-1">
                              <span>{tx.kind} · {fmtDate(tx.paid_at || tx.created_at)}</span>
                              <span className="tabular-nums">
                                {fmt(tx.amount)} € <Badge variant={tx.payment_status === "paid" ? "default" : "secondary"} className="ml-1 text-[9px]">{tx.payment_status}</Badge>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Section>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="border border-border rounded-xl p-4">
      <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-3 text-primary">
        <Icon className="w-4 h-4" />{title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-border/30 pb-1.5 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right max-w-[60%] truncate">{v || "—"}</span>
    </div>
  );
}
